<template>
  <div class="wysiwyg-editor card">
    <div v-if="editor" class="toolbar">
      <button
        @click="editor.chain().focus().toggleBold().run()"
        :class="{ 'is-active': editor.isActive('bold') }"
        type="button"
        class="toolbar-button"
        title="Bold"
      >
        <strong>B</strong>
      </button>
      
      <button
        @click="editor.chain().focus().toggleItalic().run()"
        :class="{ 'is-active': editor.isActive('italic') }"
        type="button"
        class="toolbar-button"
        title="Italic"
      >
        <em>I</em>
      </button>
      
      <button
        @click="editor.chain().focus().toggleStrike().run()"
        :class="{ 'is-active': editor.isActive('strike') }"
        type="button"
        class="toolbar-button"
        title="Strikethrough"
      >
        <s>S</s>
      </button>
      
      <button
        @click="editor.chain().focus().toggleUnderline().run()"
        :class="{ 'is-active': editor.isActive('underline') }"
        type="button"
        class="toolbar-button"
        title="Underline"
      >
        <u>U</u>
      </button>
      
      <div class="toolbar-divider"></div>
      
      <button
        @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
        :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
        type="button"
        class="toolbar-button"
        title="Heading 1"
      >
        H1
      </button>
      
      <button
        @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
        :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
        type="button"
        class="toolbar-button"
        title="Heading 2"
      >
        H2
      </button>
      
      <button
        @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
        :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
        type="button"
        class="toolbar-button"
        title="Heading 3"
      >
        H3
      </button>
      
      <button
        @click="editor.chain().focus().setParagraph().run()"
        :class="{ 'is-active': editor.isActive('paragraph') }"
        type="button"
        class="toolbar-button"
        title="Paragraph"
      >
        P
      </button>
      
      <div class="toolbar-divider"></div>
      
      <button
        @click="editor.chain().focus().setTextAlign('left').run()"
        :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
        type="button"
        class="toolbar-button"
        title="Align Left"
      >
        ⬅
      </button>
      
      <button
        @click="editor.chain().focus().setTextAlign('center').run()"
        :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }"
        type="button"
        class="toolbar-button"
        title="Align Center"
      >
        ⬆
      </button>
      
      <button
        @click="editor.chain().focus().setTextAlign('right').run()"
        :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }"
        type="button"
        class="toolbar-button"
        title="Align Right"
      >
        ➡
      </button>
      
      <button
        @click="editor.chain().focus().toggleBulletList().run()"
        :class="{ 'is-active': editor.isActive('bulletList') }"
        type="button"
        class="toolbar-button"
        title="Bullet List"
      >
        •
      </button>
      
      <button
        @click="editor.chain().focus().toggleOrderedList().run()"
        :class="{ 'is-active': editor.isActive('orderedList') }"
        type="button"
        class="toolbar-button"
        title="Ordered List"
      >
        1.
      </button>
      
      <div class="toolbar-divider"></div>
      
      <button
        @click="editor.chain().focus().toggleBlockquote().run()"
        :class="{ 'is-active': editor.isActive('blockquote') }"
        type="button"
        class="toolbar-button"
        title="Quote"
      >
        "
      </button>
      
      <button
        @click="editor.chain().focus().setHorizontalRule().run()"
        type="button"
        class="toolbar-button"
        title="Horizontal Rule"
      >
        —
      </button>
      
      <div class="toolbar-divider"></div>
      
      <button
        @click="setLink"
        :class="{ 'is-active': editor.isActive('link') }"
        type="button"
        class="toolbar-button"
        title="Insert Link"
      >
        🔗
      </button>
      
      <button
        @click="insertImage"
        type="button"
        class="toolbar-button"
        title="Insert Image"
      >
        🖼
      </button>
      
      <button
        @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()"
        type="button"
        class="toolbar-button"
        title="Insert Table"
      >
        📋
      </button>
      
      <div class="toolbar-divider"></div>
      
      <button
        @click="editor.chain().focus().undo().run()"
        :disabled="!editor.can().chain().focus().undo().run()"
        type="button"
        class="toolbar-button"
        title="Undo"
      >
        ↶
      </button>
      
      <button
        @click="editor.chain().focus().redo().run()"
        :disabled="!editor.can().chain().focus().redo().run()"
        type="button"
        class="toolbar-button"
        title="Redo"
      >
        ↷
      </button>
    </div>
    
    <editor-content :editor="editor" class="editor-content" />
  </div>
</template>

<script>
import { computePosition } from '@floating-ui/dom'
import { Editor, EditorContent, VueRenderer } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { TableKit } from '@tiptap/extension-table'
import Emoji, { gitHubEmojis } from '@tiptap/extension-emoji'
import EmojiList from './EmojiList.vue'

export default {
  components: {
    EditorContent,
  },

  props: {
    value: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      editor: null,
    }
  },

  watch: {
    value(value) {
      const isSame = this.editor.getHTML() === value

      if (isSame) {
        return
      }

      this.editor.commands.setContent(value, false)
    },
  },

  methods: {
    setLink() {
      const previousUrl = this.editor.getAttributes('link').href
      const url = window.prompt('URL', previousUrl)

      // cancelled
      if (url === null) {
        return
      }

      // empty
      if (url === '') {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
        return
      }

      // update link
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url, rel: null }).run()
    },

    insertImage() {
      const url = window.prompt('Enter image URL')
      
      if (url) {
        this.editor.chain().focus().setImage({ src: url }).run()
      }
    },
  },

  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        StarterKit,
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Link.configure({
          openOnClick: false,
          defaultProtocol: 'https',
          HTMLAttributes: {
            rel: null,
          },
        }),
        Image,
        TableKit.configure({
          table: { resizable: true },
        }),
        Emoji.configure({
          emojis: gitHubEmojis,
          suggestion: {
            char: ':',
            items: ({ editor, query }) => {
              return editor.storage.emoji.emojis
              .filter(({ shortcodes, tags }) => {
                return (
                  shortcodes.find(shortcode => shortcode.startsWith(query.toLowerCase())) ||
                  tags.find(tag => tag.startsWith(query.toLowerCase()))
                )
              })
              .slice(0, 10)
            },
            render: () => {
              let component

              function repositionComponent(clientRect) {
                if (!component || !component.element) {
                  return
                }

                const virtualElement = {
                  getBoundingClientRect() {
                    return clientRect
                  },
                }

                computePosition(virtualElement, component.element, {
                  placement: 'bottom-start',
                }).then(pos => {
                  Object.assign(component.element.style, {
                    left: `${pos.x}px`,
                    top: `${pos.y}px`,
                    position: pos.strategy === 'fixed' ? 'fixed' : 'absolute',
                  })
                })
              }

              return {
                onStart: props => {
                  component = new VueRenderer(EmojiList, {
                    props,
                    editor: props.editor,
                  })

                  document.body.appendChild(component.element)
                  repositionComponent(props.clientRect())
                },

                onUpdate(props) {
                  component.updateProps(props)
                  repositionComponent(props.clientRect())
                },

                onKeyDown(props) {
                  if (props.event.key === 'Escape') {
                    document.body.removeChild(component.element)
                    component.destroy()

                    return true
                  }

                  return component.ref?.onKeyDown(props)
                },

                onExit() {
                  if (document.body.contains(component.element)) {
                    document.body.removeChild(component.element)
                  }
                  component.destroy()
                },
              }
            },
          },
        }),
      ],
      onUpdate: () => {
        this.$emit('input', this.editor.getHTML())
      },
    })
  },

  beforeDestroy() {
    this.editor.destroy()
  },
}
</script>

<style scoped>
.wysiwyg-editor {
  border-radius: 0.375rem;
  background-color: white;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  padding: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
  border-top-left-radius: 0.375rem;
  border-top-right-radius: 0.375rem;
  flex-wrap: wrap;
}

.toolbar-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.5rem;
  margin: 0.125rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  background-color: white;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  min-width: 2rem;
  height: 2rem;
}

.toolbar-button:hover {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.toolbar-button:active {
  background-color: #e5e7eb;
}

.toolbar-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toolbar-button.is-active {
  background-color: #3b82f6;
  color: white;
  border-color: #2563eb;
}

.toolbar-button.is-active:hover {
  background-color: #2563eb;
}

.toolbar-divider {
  width: 1px;
  height: 1.5rem;
  background-color: #d1d5db;
  margin: 0 0.25rem;
}

.editor-content {
  padding: 0.75rem;
  min-height: 150px;
}

.editor-content .ProseMirror {
  outline: none;
  line-height: 1.6;
}

.editor-content .ProseMirror:focus-visible,
.ProseMirror-focused {
  outline: none;
}

.editor-content .ProseMirror h1 {
  font-size: 1.875rem;
  font-weight: 700;
  margin: 1rem 0 0.5rem 0;
}

.editor-content .ProseMirror h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem 0;
}

.editor-content .ProseMirror h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem 0;
}

.editor-content .ProseMirror p {
  margin: 0.5rem 0;
}

.editor-content .ProseMirror ul,
.editor-content .ProseMirror ol {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.editor-content .ProseMirror li {
  margin: 0.25rem 0;
}

.editor-content .ProseMirror blockquote {
  border-left: 4px solid #e5e7eb;
  margin: 1rem 0;
  padding-left: 1rem;
  color: #6b7280;
  font-style: italic;
}

.editor-content .ProseMirror hr {
  border: none;
  border-top: 2px solid #e5e7eb;
  margin: 1.5rem 0;
}

.editor-content .ProseMirror strong {
  font-weight: 700;
}

.editor-content .ProseMirror em {
  font-style: italic;
}

.editor-content .ProseMirror s {
  text-decoration: line-through;
}

.editor-content .ProseMirror u {
  text-decoration: underline;
}

.editor-content .ProseMirror code {
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-family: 'Monaco', 'Consolas', 'Courier New', monospace;
  font-size: 0.875rem;
}

.editor-content .ProseMirror pre {
  background-color: #1f2937;
  color: #f9fafb;
  padding: 1rem;
  border-radius: 0.375rem;
  overflow-x: auto;
  margin: 1rem 0;
}

.editor-content .ProseMirror pre code {
  background-color: transparent;
  color: inherit;
  padding: 0;
}

/* Text alignment styles */
.editor-content .ProseMirror[style*="text-align: left"],
.editor-content .ProseMirror p[style*="text-align: left"],
.editor-content .ProseMirror h1[style*="text-align: left"],
.editor-content .ProseMirror h2[style*="text-align: left"],
.editor-content .ProseMirror h3[style*="text-align: left"] {
  text-align: left;
}

.editor-content .ProseMirror[style*="text-align: center"],
.editor-content .ProseMirror p[style*="text-align: center"],
.editor-content .ProseMirror h1[style*="text-align: center"],
.editor-content .ProseMirror h2[style*="text-align: center"],
.editor-content .ProseMirror h3[style*="text-align: center"] {
  text-align: center;
}

.editor-content .ProseMirror[style*="text-align: right"],
.editor-content .ProseMirror p[style*="text-align: right"],
.editor-content .ProseMirror h1[style*="text-align: right"],
.editor-content .ProseMirror h2[style*="text-align: right"],
.editor-content .ProseMirror h3[style*="text-align: right"] {
  text-align: right;
}

/* Link styles */
.editor-content .ProseMirror a {
  color: #3b82f6;
  text-decoration: underline;
  cursor: pointer;
}

.editor-content .ProseMirror a:hover {
  color: #2563eb;
}

/* Image styles */
.editor-content .ProseMirror img {
  max-width: 100%;
  height: auto;
  border-radius: 0.375rem;
  margin: 0.5rem 0;
}

/* Table styles */
.editor-content .ProseMirror table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 1rem 0;
  overflow: hidden;
}

.editor-content .ProseMirror table td,
.editor-content .ProseMirror table th {
  min-width: 1em;
  border: 2px solid #e5e7eb;
  padding: 0.5rem;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}

.editor-content .ProseMirror table th {
  font-weight: bold;
  text-align: left;
  background-color: #f9fafb;
}

.editor-content .ProseMirror table .selectedCell:after {
  z-index: 2;
  position: absolute;
  content: "";
  left: 0; right: 0; top: 0; bottom: 0;
  background: rgba(200, 200, 255, 0.4);
  pointer-events: none;
}

[data-type='emoji'] {
  img {
    height: 1em;
    width: 1em;
  }
}

/* Emoji suggestion dropdown styles */
.emoji-suggestions {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-height: 200px;
  overflow-y: auto;
  padding: 0.25rem;
  z-index: 1000;
}

.emoji-suggestion-item {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.emoji-suggestion-item:hover,
.emoji-suggestion-item.is-selected {
  background-color: #f3f4f6;
}

.emoji-suggestion-item .emoji {
  margin-right: 0.5rem;
  font-size: 1.25rem;
}

.emoji-suggestion-item .name {
  color: #6b7280;
  font-size: 0.875rem;
  font-family: monospace;
}
</style>
